// src/finance/expenseSync.ts
// Diff pur entre l'état local de l'éditeur et la liste serveur des récurrentes.
// La key d'une ligne hydratée EST l'id serveur ; une key inconnue = nouvelle ligne.
//
// ⚠️ L'appelant DOIT pré-filtrer `server` par `kind` : `deletes` est aveugle au kind
// (toute ligne serveur absente de `local` est supprimée). Un appel avec la liste
// tous kinds confondus effacerait donc les récurrentes de l'autre kind.
//
// Une ligne au label vide (ou blanc) n'est ni créée ni mise à jour : elle reste
// purement locale jusqu'à ce qu'elle porte un label. Le serveur rejette `label: ''`
// (400 Zod) — vider un label est un geste courant, il ne doit pas lever d'erreur.
import type { RecurringExpense } from '../lib/types'
import type { EditableExpenseLine } from './components/ExpenseListEditor'

export type ExpenseDiff = {
  creates: EditableExpenseLine[]
  updates: EditableExpenseLine[]
  deletes: string[]
}

/**
 * Résout les keys UI vers les ids serveur connus (table alimentée après chaque
 * création). La key UI d'une ligne ne change JAMAIS — c'est elle qui sert de key
 * React : la remplacer démonterait l'input en cours de frappe et volerait le
 * focus (bug du 2026-08-20). La correspondance vit ici, au moment du sync.
 */
export function resolveLineKeys(
  local: EditableExpenseLine[],
  serverIdByKey: ReadonlyMap<string, string>,
): EditableExpenseLine[] {
  return local.map((l) => {
    const serverId = serverIdByKey.get(l.key)
    return serverId === undefined ? { ...l } : { ...l, key: serverId }
  })
}

export function diffRecurringExpenses(
  local: EditableExpenseLine[],
  server: RecurringExpense[],
): ExpenseDiff {
  const serverById = new Map(server.map((e) => [e.id, e]))
  const localKeys = new Set(local.map((l) => l.key))

  const creates = local.filter((l) => !serverById.has(l.key) && l.label.trim() !== '')
  const updates = local.filter((l) => {
    const existing = serverById.get(l.key)
    if (!existing) return false
    if (l.label.trim() === '') return false
    return (
      existing.label !== l.label ||
      existing.amount !== l.amount ||
      existing.active !== l.active ||
      existing.periodicity !== l.periodicity ||
      existing.dueMonth !== l.dueMonth
    )
  })
  const deletes = server.filter((e) => !localKeys.has(e.id)).map((e) => e.id)
  return { creates, updates, deletes }
}
